import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMyDetailsDetailed } from '../../../../redux/api/users/usersApi';
import { MeasurementParameter } from '../../../../types/interfaces/user-measurement';
import {
  findLatestMeasurement,
  formatMeasurementDisplay,
} from '../../../../utils/measurements/measurement-utils';

export function useMeasurementsView() {
  const { t } = useTranslation();
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchQuery,
  } = useGetMyDetailsDetailed();
  const [activeParameter, setActiveParameter] =
    useState<MeasurementParameter | null>(null);

  const latestMeasurements = data?.latestMeasurements;

  const weightMeasurement = useMemo(
    () => findLatestMeasurement(latestMeasurements, 'weight'),
    [latestMeasurements],
  );

  const heightMeasurement = useMemo(
    () => findLatestMeasurement(latestMeasurements, 'height'),
    [latestMeasurements],
  );

  const notSetLabel = t('common.notSet');

  const weightDisplay = useMemo(
    () => formatMeasurementDisplay(weightMeasurement, notSetLabel),
    [weightMeasurement, notSetLabel],
  );

  const heightDisplay = useMemo(
    () => formatMeasurementDisplay(heightMeasurement, notSetLabel),
    [heightMeasurement, notSetLabel],
  );

  const activeLatestMeasurement = useMemo(() => {
    if (activeParameter === 'weight') {
      return weightMeasurement;
    }

    if (activeParameter === 'height') {
      return heightMeasurement;
    }

    return undefined;
  }, [activeParameter, weightMeasurement, heightMeasurement]);

  const handleRecordClick = useCallback((parameter: MeasurementParameter) => {
    setActiveParameter(parameter);
  }, []);

  const handleModalClose = useCallback(() => {
    setActiveParameter(null);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setActiveParameter(null);
  }, []);

  const errors = isError && error ? [error] : [];

  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  return {
    isLoading,
    isError,
    errors,
    refetch,
    weightDisplay,
    heightDisplay,
    activeParameter,
    activeLatestMeasurement,
    handleRecordClick,
    handleModalClose,
    handleModalSuccess,
  };
}
